import React from 'react';
import { Database } from 'lucide-react';

/**
 * DataSourceLine — Trust Infrastructure component.
 *
 * Renders the small italic citation line under every chart / numeric output.
 * Format: "Source: IMD AWS Jodhpur · NASA POWER · Last updated: 2026-02-15"
 *
 * Props:
 *   sources: array of strings or {name, url}. Required.
 *   lastUpdated: ISO date string (yyyy-mm-dd) or null. Optional.
 */
function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

export default function DataSourceLine({ sources = [], lastUpdated = null }) {
  if (!sources || sources.length === 0) return null;
  const formatted = fmtDate(lastUpdated);
  return (
    <div
      className="font-body inline-flex items-center gap-1.5 flex-wrap"
      style={{
        fontSize: 11.5,
        color: '#6F6A63',
        fontStyle: 'italic',
        lineHeight: 1.5,
      }}
      data-testid="data-source-line"
    >
      <Database size={11} style={{ color: '#8A8580', flexShrink: 0 }} />
      <span style={{ fontWeight: 500, fontStyle: 'normal', color: '#8A8580', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 10 }}>
        Source
      </span>
      {sources.map((s, i) => {
        const obj = typeof s === 'string' ? { name: s } : s;
        return (
          <React.Fragment key={`${obj.name}-${i}`}>
            {i > 0 && <span style={{ color: '#C5BEB4' }}>·</span>}
            {obj.url ? (
              <a
                href={obj.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#3F4A3D', textDecoration: 'underline', textDecorationColor: '#C5BEB4', textUnderlineOffset: 2 }}
              >
                {obj.name}
              </a>
            ) : (
              <span style={{ color: '#3F4A3D' }}>{obj.name}</span>
            )}
          </React.Fragment>
        );
      })}
      {formatted && (
        <>
          <span style={{ color: '#C5BEB4' }}>·</span>
          <span style={{ color: '#6F6A63' }}>Last updated: {formatted}</span>
        </>
      )}
    </div>
  );
}
